import React, { useState, useEffect, useContext } from "react";
import { API, AuthContext } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SubscribeDialog } from "./demo/SubscribeDialog";

export default function BillingSettings() {
  const { refreshUser } = useContext(AuthContext);
  const [sub, setSub] = useState(undefined); // undefined=loading, null=none
  const [showCancel, setShowCancel] = useState(false);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [plans, setPlans] = useState(null);

  const load = async () => {
    const res = await fetch(`${API}/billing/subscription`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (res.ok) setSub((await res.json()).subscription);
  };
  useEffect(() => { load(); fetch(`${API}/billing/plans`).then((r) => r.json()).then(setPlans).catch(() => {}); }, []); // eslint-disable-line

  const portal = async () => {
    toast.info("Manage Billing (card, invoices) opens the Stripe Customer Portal once live keys are connected.");
  };

  const cancel = async () => {
    setShowCancel(false);
    const res = await fetch(`${API}/billing/cancel`, {
      method: "POST",
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    });
    if (res.ok) {
      const d = await res.json();
      toast.success(`Cancelled. Access continues until ${new Date(d.active_until).toLocaleDateString("en-AU")}.`);
      load();
    } else toast.error("Could not cancel");
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="billing-settings-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
          <CreditCard className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Billing</h1>
      </div>

      {sub === undefined ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600" /></div>
      ) : !sub || sub.status !== "active" ? (
        (() => {
          const m = plans?.monthly, a = plans?.annual;
          const monthsFree = m && a && m.amount ? Math.round((m.amount * 12 - a.amount) / m.amount) : 0;
          const saving = m && a ? Math.round(m.amount * 12 - a.amount) : 0;
          const perMo = a ? (a.amount / 12).toFixed(2) : null;
          return (
            <Card>
              <CardContent className="py-7">
                <p className="text-center text-slate-600">You don't have an active subscription.</p>
                {a && m && (
                  <div className="mt-5 grid sm:grid-cols-2 gap-3">
                    {/* Annual — highlighted */}
                    <div className="relative rounded-xl border-2 border-emerald-300 bg-emerald-50/60 p-4" data-testid="billing-plan-annual">
                      <span className="absolute -top-2.5 left-4 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm" data-testid="billing-annual-ribbon">
                        MOST POPULAR{monthsFree > 0 ? ` · ${monthsFree} MONTHS FREE` : ""}
                      </span>
                      <p className="text-sm font-semibold text-slate-700">Annual</p>
                      <p className="mt-1"><span className="text-2xl font-bold text-slate-900">${a.amount}</span><span className="text-slate-500 text-sm"> {a.currency?.toUpperCase()}/yr</span></p>
                      <p className="text-xs font-medium text-emerald-700 mt-1">Save ${saving}/yr · just ${perMo}/mo billed yearly</p>
                    </div>
                    {/* Monthly */}
                    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="billing-plan-monthly">
                      <p className="text-sm font-semibold text-slate-700">Monthly</p>
                      <p className="mt-1"><span className="text-2xl font-bold text-slate-900">${m.amount}</span><span className="text-slate-500 text-sm"> {m.currency?.toUpperCase()}/mo</span></p>
                      <p className="text-xs text-slate-400 mt-1">Flexible month-to-month · GST inc</p>
                    </div>
                  </div>
                )}
                <div className="text-center">
                  <Button className="mt-5 bg-amber-500 hover:bg-amber-600" onClick={() => setShowSubscribe(true)} data-testid="billing-subscribe-btn">
                    Subscribe
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })()
      ) : (
        <Card>
          <CardContent className="py-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-slate-400">Current plan</p>
                <p className="text-lg font-bold text-slate-900" data-testid="billing-plan-name">
                  {sub.price_tier_name} — ${sub.amount} {sub.currency?.toUpperCase()}/{sub.billing_interval === "annual" ? "yr" : "mo"}
                </p>
              </div>
              {sub.cancel_at_period_end ? (
                <Badge className="bg-rose-100 text-rose-700 border-0" data-testid="billing-status-badge">Cancelling</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-700 border-0" data-testid="billing-status-badge">Active</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400">{sub.cancel_at_period_end ? "Access until" : "Next billing date"}</p>
                <p className="font-medium text-slate-800" data-testid="billing-next-date">{fmtDate(sub.current_period_end)}</p>
              </div>
              <div>
                <p className="text-slate-400">GST</p>
                <p className="font-medium text-slate-800">Included</p>
              </div>
            </div>
            {sub.cancel_at_period_end && (
              <p className="text-sm text-rose-600 bg-rose-50 rounded-lg p-3">
                Your subscription is set to cancel. Your data will be retained for 30 days after access ends, then deleted.
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={portal} data-testid="billing-manage-btn">Manage Billing</Button>
              {!sub.cancel_at_period_end && (
                <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => setShowCancel(true)} data-testid="billing-cancel-btn">
                  Cancel Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={showCancel} onOpenChange={setShowCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel your subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You'll keep full access until the end of your current billing period. After that your data is
              retained for 30 days, then permanently deleted. You can resubscribe anytime before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} className="bg-rose-600 hover:bg-rose-700" data-testid="billing-confirm-cancel-btn">
              Cancel Subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SubscribeDialog open={showSubscribe} onClose={() => { setShowSubscribe(false); load(); }} />
    </div>
  );
}
